import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { Button, Row, Col } from 'react-bootstrap';
import { compose, branch, renderNothing } from 'recompose';
import { connect } from 'react-redux';
import ReactTable from 'react-table';

import { getDateApiFormat, getShowCompletedJobs } from '../selectors';
import { getApiServerUrl } from '../../configuration/selectors';
import { queryGetAccrualReportGQL } from '../graphql/getAccrualReportApi';
import { defaultTableFiltering, startsWithTableFiltering } from '../../../utils';
import linkWithCredentials from '../../../utils/linkWithCredentials';
import { downloadingFileAndUpdatingCredentials } from '../../../utils/downloadUpdatingCredentials';

const propTypes = exact({
  startDate: PropTypes.string,
  showCompletedJobs: PropTypes.bool.isRequired,
  apiServerUrl: PropTypes.string.isRequired,

  loading: PropTypes.bool.isRequired,
  accrualReportData: PropTypes.array.isRequired,
});


const DataTable = (props) => {
  const {
    apiServerUrl,
    startDate,
    accrualReportData,
    loading,
    showCompletedJobs,
  } = props;

  const noData = () => {
    if (loading) {
      return null;
    }
    return <div className="rt-noData">No rows found</div>;
  };

  const openUrl = () => {
    const urlCred = linkWithCredentials(`${apiServerUrl}/accrual_report`);
    const csvUrl = (`${urlCred}&start_date=${startDate}&show_completed=${showCompletedJobs}&with_headings=true`);
    downloadingFileAndUpdatingCredentials(csvUrl);
  };

  return (
    <React.Fragment>
      <Row>
        <Col md={12}>
          <Button
            onClick={openUrl}
            bsStyle="primary"
            className="pull-right"
          >
            Download Report
          </Button>
        </Col>
      </Row>
      <ReactTable
        data={accrualReportData}
        loading={loading}
        NoDataComponent={noData}
        filterable
        defaultFilterMethod={(filter, row) => {
          return defaultTableFiltering(filter, row);
        }}
        columns={[
          {
            Header: 'State Changed At',
            id: 'stateChangeAt',
            accessor: (e) => { return e.stateChangeAt; },
          },
          {
            Header: 'Job Number',
            id: 'jobNumber',
            accessor: (e) => { return e.jobNumber; },
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: filter.id,
                value: filter.value,
              };
              return startsWithTableFiltering(customFilterObj, row);
            },
          },
          {
            Header: 'State',
            id: 'state',
            accessor: (e) => { return e.state; },
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: filter.id,
                value: filter.value,
              };
              return startsWithTableFiltering(customFilterObj, row);
            },
          },
          {
            Header: 'Accounting State',
            id: 'accountingState',
            accessor: (e) => { return e.accountingState; },
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: filter.id,
                value: filter.value,
              };
              return startsWithTableFiltering(customFilterObj, row);
            },
          },
          {
            Header: 'First Date',
            id: 'firstDate',
            accessor: (e) => { return e.firstDate; },
          },
          {
            Header: 'Latest Date In (or before) Period',
            id: 'latestDateInOrBeforePeriod',
            accessor: (e) => { return e.latestDateInOrBeforePeriod; },
          },
          {
            Header: 'Number Of Tickets Accumlated',
            id: 'numberOfTicketsAccumlated',
            accessor: (e) => { return e.numberOfTicketsAccumlated; },
          },
          {
            Header: 'Accumulated Estimated Net Revenue',
            id: 'accumulatedEstimatedNetRevenue',
            accessor: (e) => { return e.accumulatedEstimatedNetRevenue; },
            Cell: (row) => {
              const revenue = row.original.accumulatedEstimatedNetRevenue;
              return (
                <div>
                  {revenue.toFixed(2)}
                </div>
              );
            },
          },
          {
            Header: 'District',
            id: 'districtName',
            accessor: (e) => { return e.districtName; },
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: filter.id,
                value: filter.value,
              };
              return startsWithTableFiltering(customFilterObj, row);
            },
          },
          {
            Header: 'Department',
            id: 'departmentName',
            accessor: (e) => { return e.departmentName; },
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: filter.id,
                value: filter.value,
              };
              return startsWithTableFiltering(customFilterObj, row);
            },
          },
        ]}
        defaultPageSize={20}
        className="-striped -highlight"
      />
    </React.Fragment>
  );
};


DataTable.propTypes = propTypes;

const withNoStartDate = branch(
  (props) => { return props.startDate === null; },
  renderNothing,
);

const mapStateToProps = (state) => {
  return {
    startDate: getDateApiFormat(state),
    showCompletedJobs: getShowCompletedJobs(state),
    apiServerUrl: getApiServerUrl(state),
  };
};

const withRedux = connect(mapStateToProps, {});

const enhance = compose(
  withRedux,
  withNoStartDate,
  queryGetAccrualReportGQL,
);

export default enhance(DataTable);
